import React, { useEffect, useRef, useState } from 'react';
import {
    CommandBar,
    ContextualMenuItemType,
    ICommandBarItemProps,
    IContextualMenuItem,
    Stack,
    StackItem
} from '@fluentui/react';

import DataGrid from "../DataGrid/DataGrid";
import { ILooseCollectionResponse } from "../../models/ICollectionResponse";
import { GridSelectionMode, IColumnDetails } from "../../models/GridConfiguration";
import Sticky from "react-stickynode";
import GridColumnEditModal from "./GridColumnEditModal";
import {
    useGetAvailableGridConfigurationsQuery, useGetGridAssignmentQuery,
    useGetGridConfigurationQuery,
    useSaveGridAssignmentMutation
} from "../../store/Api";
import CreateCsvPanel from "./CreateCsvPanel";
import useCan, { REPORT_GENERATE, REPORT_READ } from "../../permissions/ability"
import SelectedDisplay from '../Search/SelectedDisplay';

interface IGridComposerProps {
    columnSetName: string;
    data: ILooseCollectionResponse;
    loading: boolean;
    filter?: string;
    filterField?: string;
    sortBy?: string;
    sortDirection?: string;
    onSort: (column?: string, sortDirection?: string) => void;
    onFilter?: (filter?: string, column?: string) => void;
    overrideColumns?: IColumnDetails[]
    canDragColumns?: boolean;
    canResizeColumns?: boolean;
    canCustomizeColumns?: boolean;
    canChangePageSize?: boolean;
    canCreateCsv?: boolean;
    /**
     * Let the table grow to its content instead of sizing itself to the bottom of the VIEWPORT.
     *
     * The default sizing assumes the window is what scrolls. Inside a host that scrolls on its own —
     * the settings layout's content area, a panel — that assumption overshoots the container, so the
     * host scrolls as well as the table and you get two scrollbars. Set this there and the page keeps
     * a single scrollbar.
     */
    useContentHeight?: boolean;
    initialPageSize?: number;
    filters?: { [key: string]: string }
    onFiltersChange?: (filters: { [key: string]: string }) => void;
    additionalCommands?: ICommandBarItemProps[];
    additionalFarCommands?: ICommandBarItemProps[];
    showFiltersAsTags?: boolean;
    onRefresh?: () => void;

    page: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;

    selectionMode: GridSelectionMode;
    selectedItems?: string[];
    onSelectedItemsChange?: (selectedItems: string[]) => void;
    getItemId: (item: any) => string;
}
const GridComposer: React.FC<IGridComposerProps> = (props) => {
    const [totalItemCount, setTotalItemCount] = useState<number>()
    const [pageSize, setPageSize] = useState<number>(25)
    const [editColumnsModalOpen, setEditColumnsModalOpen] = useState<boolean>()
    const [effectiveItems, setEffectiveItems] = useState<any[]>()
    const [saveGridAssignment] = useSaveGridAssignmentMutation();
    const [gridAssignment, setGridAssignment] = useState<string | undefined>()
    const [displayCsvModal, setDisplayCsvModal] = useState(false)
    const defaultSortApplied = useRef<string | undefined>(undefined)
    const projectedColumnsSignature = useRef<string | undefined>(undefined)
    const getColumns = useGetGridConfigurationQuery({ gridIdentifier: props.columnSetName, gridIdHint: gridAssignment ?? '' }, { skip: (!props.columnSetName || props.columnSetName === '' || !gridAssignment || gridAssignment === '') });
    const getColumnSets = useGetAvailableGridConfigurationsQuery(props.columnSetName, { skip: (!props.columnSetName || props.columnSetName === '') });
    const getGridAssignment = useGetGridAssignmentQuery(props.columnSetName, { skip: (!props.columnSetName || props.columnSetName === '') });

    useEffect(() => {
        if (getGridAssignment.data) {
            setGridAssignment(getGridAssignment.data?.assignment)
        }
    }, [getGridAssignment.data])
    useEffect(() => {
        if (props.initialPageSize) {
            setPageSize(props.initialPageSize)
        }
    }, [props.initialPageSize])

    // Apply the grid's configured default sort once the column metadata loads,
    // unless the user has already chosen a sort. Tracked per grid + assignment so
    // switching configurations re-evaluates the default.
    useEffect(() => {
        const key = `${props.columnSetName}:${gridAssignment ?? ''}`
        if (defaultSortApplied.current === key) return
        if (props.sortBy) {
            defaultSortApplied.current = key
            return
        }
        const columns = getColumns.data?.columnDetails
        if (!columns || columns.length === 0) return
        const defaultColumn = columns.find(c => c.defaultSort)
        defaultSortApplied.current = key
        if (defaultColumn) {
            props.onSort(defaultColumn.identifier, defaultColumn.defaultSortDirection ?? 'asc')
        }
    }, [getColumns.data, gridAssignment, props.columnSetName, props.sortBy])

    // The API projects each list row's fields to the columns saved on the grid config, so a
    // newly added column arrives with no data in the already-fetched results — it stays blank
    // until the list query re-runs. When the visible column set changes (add a column, switch
    // saved column sets), refetch the data so the new column populates at the same time it appears.
    useEffect(() => {
        const visibleColumns = getColumns.data?.visibleColumns
        if (!visibleColumns) return
        const signature = visibleColumns.join(',')
        if (projectedColumnsSignature.current === undefined) {
            // First resolved config — nothing to refresh, just record the baseline.
            projectedColumnsSignature.current = signature
            return
        }
        if (projectedColumnsSignature.current === signature) return
        projectedColumnsSignature.current = signature
        props.onRefresh?.()
    }, [getColumns.data])

    const updateGridAssignment = (gridAssignment?: string) => {
        setGridAssignment(() => gridAssignment)
        if (gridAssignment) {
            saveGridAssignment({ gridIdentifier: props.columnSetName, gridId: gridAssignment })
        }
    }

    const changePage = (page: number) => {
        props.onPageChange(page)
    }
    const changePageSize = (pageSize: number) => {
        setPageSize(pageSize)
        props.onPageSizeChange(pageSize)
    }
    useEffect(() => {
        const currentData = props.data

        setEffectiveItems(() => [...currentData.items ?? []])
        setTotalItemCount(currentData.total)
    }, [props.page, pageSize, props.data]);

    const getStartCount = () => {
        if (totalItemCount && props.page && pageSize) {
            return (props.page * pageSize) - (pageSize - 1)
        }
        return 0
    }
    const getEndCount = () => {
        if (totalItemCount) {
            const max = props.page * pageSize
            return max > totalItemCount ? totalItemCount : max
        }
    }

    const pages = totalItemCount && pageSize ? Math.ceil(totalItemCount / pageSize) : 0;
    const pageCommands: ICommandBarItemProps[] = Array.from({ length: Math.min(pages, 10) }, (_, i) => ({
        key: `page-${i + 1}`,
        text: `Page ${i + 1}`,
        checked: props.page === i + 1,
        onClick: () => changePage(i + 1),
        disabled: props.page === i + 1
    }))

    const rightCommands: ICommandBarItemProps[] = [
        ...(props.additionalFarCommands ?? []),
        ...(props.onRefresh ? [{
            key: 'refresh',
            text: 'Refresh',
            iconOnly: true,
            iconProps: { iconName: 'Refresh' },
            disabled: props.loading,
            onClick: () => props.onRefresh?.()
        }] : []),
        {
            key: 'previous',
            text: 'Previous',
            iconOnly: true,
            iconProps: { iconName: 'ChevronLeft' },
            disabled: props.page <= 1,
            onClick: () => changePage(props.page - 1)
        },
        {
            key: 'page',
            text: totalItemCount && totalItemCount > 0 ? `${getStartCount()}-${getEndCount()} of ${totalItemCount}` : 'No Results',
            subMenuProps: props.canChangePageSize || pages > 1 ? {
                items: [
                    ...pageCommands,
                    ...(pageCommands.length > 0 && props.canChangePageSize ? [{
                        key: "divider",
                        itemType: ContextualMenuItemType.Divider
                    }] : []),
                    ...(props.canChangePageSize ? [
                    {
                        key: "sectionTitlePageSize",
                        text: "Select Page Size",
                        itemType: ContextualMenuItemType.Header
                    },
                    {
                        key: "pageSize25",
                        text: "25",
                        onClick: () => changePageSize(25),
                        disabled: pageSize === 25
                    },
                    {
                        key: "pageSize50",
                        text: "50",
                        onClick: () => changePageSize(50),
                        disabled: pageSize === 50
                    },
                    {
                        key: "pageSize100",
                        text: "100",
                        onClick: () => changePageSize(100),
                        disabled: pageSize === 100
                    },
                    {
                        key: "pageSize250",
                        text: "250",
                        onClick: () => changePageSize(250),
                        disabled: pageSize === 250
                    }] : [])
                ]
            } : undefined
        },
        {
            key: 'next',
            text: 'Next',
            iconOnly: true,
            iconProps: { iconName: 'ChevronRight' },
            disabled: totalItemCount ? props.page >= Math.ceil(totalItemCount / pageSize) : true,
            onClick: () => changePage(props.page + 1)
        }
    ]

    const splitCommandEntries: IContextualMenuItem[] = [
        {
            key: "sectionTitle",
            text: "Available Column Configurations",
            itemType: ContextualMenuItemType.Header
        },
        ...getColumnSets.data?.map((item) => (
            {
                key: item.id ?? '',
                disabled: item.id === getColumns.data?.id,
                text: item.name,
                onClick: () => { updateGridAssignment(item.id) },
            })) ?? []
    ]

    const canGenerateReport = useCan(REPORT_GENERATE);
    const canReadReport = useCan(REPORT_READ);

    const commands: ICommandBarItemProps[] = [
        {
            key: 'customize',
            text: 'Customize Columns',
            iconProps: { iconName: 'edit' },
            onClick: () => setEditColumnsModalOpen(true),
            subMenuProps: {
                items: splitCommandEntries,
            },
            split: true
        },
        ...(props.additionalCommands ?? [])
    ]
    if (props.canCreateCsv && canGenerateReport && canReadReport) {
        const csvCommand = {
            key: 'GenerateCsv',
            text: 'Export to CSV',
            iconProps: { iconName: 'Export' },
            onClick: () => {
                setDisplayCsvModal(true)
            }
        }
        commands.push(csvCommand)
    }

    //TODO: when you clear out the filters, it leaves the last character

    return (<div style={{ overflowX: 'auto', width: '100%' }}>
        {props.canCustomizeColumns &&
            <Sticky enabled={true} top={0}>
                <CommandBar
                    items={commands}
                    farItems={rightCommands}
                />
            </Sticky>
        }
        {props.showFiltersAsTags && props.filters && Object.keys(props.filters).length > 0 && <Stack horizontal wrap tokens={{ childrenGap: '1em' }} style={{ marginBottom: '1em' }}>
            {Object.entries(props.filters).map(([key, value]) => (
                <StackItem>
                    <SelectedDisplay
                        key={key}
                        prefix={getColumns.data?.columnDetails?.find(col => col.identifier === key || col.identifier.replace('.keyword','') === key)?.label ?? key}
                        selections={[value]}
                        onChange={() => {
                            const newFilters = { ...props.filters }
                            delete newFilters[key]
                            props.onFiltersChange?.(newFilters)
                        }}
                    />
                </StackItem>
            ))}
        </Stack>}
        <DataGrid
            useContentHeight={props.useContentHeight}
            selectionMode={props.selectionMode}
            getItemId={props.getItemId}
            selectedItems={props.selectedItems}
            onSelectionChange={props.onSelectedItemsChange}
            stickyHeader
            enableShimmer={props.loading || getGridAssignment.isFetching || getGridAssignment.isLoading || getColumnSets.isFetching || getColumnSets.isLoading}
            items={effectiveItems ?? []}
            gridSource={props.columnSetName}
            gridId={gridAssignment}
            loading={props.loading || getGridAssignment.isFetching || getGridAssignment.isLoading || getColumnSets.isFetching || getColumnSets.isLoading}
            filters={props.filters}
            onFiltersChange={props.onFiltersChange}
            onFilterChange={(filter, field) => {
                changePage(1)
                props.onFilter?.(filter, field);
            }}
            sortDirection={props.sortDirection}
            sortBy={props.sortBy}
            onSortChange={(column, sortDirection) => {
                changePage(1)
                props.onSort(column, sortDirection)
            }}
            overrideColumns={props.overrideColumns}
            canDrag={props.canDragColumns}
            canResize={props.canResizeColumns}
        />
        <GridColumnEditModal
            setName={props.columnSetName}
            isOpen={editColumnsModalOpen == true}
            onClose={() => {
                setEditColumnsModalOpen(false)
            }}
            onSave={(columns, assignment) => {
                setEditColumnsModalOpen(false)
                if (assignment) {
                    setGridAssignment(assignment)
                }
            }}
        />
        <CreateCsvPanel
            gridIdentifier={props.columnSetName}
            isOpen={displayCsvModal}
            onClose={() => { setDisplayCsvModal(false) }}
        />

        {props.children}
    </div>
    );
}

export default GridComposer;